import { Component } from '@theme/component';
import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';

const selectors = {
  ITEM_COUNT_VAR: '--item-count',
  NAV_CONTAINER: '.carousel__buttons',
  SCROLL_PROGRESS_VAR: '--scroll-progress',
  PROGRESS_CLASS: 'carousel__progress-item',
  PROGRESS_SELECTED_CLASS: 'carousel__progress-item--selected',
};

/**
 * A custom element that renders an Embla Carousel with prev/next buttons, progress indicators (dots/slider), and autoplay.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} viewport - The carousel viewport element.
 * @property {HTMLButtonElement} prevButton - The previous button element.
 * @property {HTMLButtonElement} nextButton - The next button element.
 * @property {HTMLElement} progress - The progress container element.
 *
 * @extends {Component<Refs>}
 */
class Carousel extends Component {
  requiredRefs = ['viewport'];

  /** @type {EmblaCarousel.globalOptions | null} */
  #emblaApi = null;

  /** @type {HTMLButtonElement[]} */
  #progressNodes = [];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyCarousel();
  }

  /* ============================================================
   * Carousel Initialization & Destruction
   * ============================================================ */

  #initCarousel() {
    const { viewport } = this.refs;
    const { align, dragFree, loop, autoplay } = this.dataset;
    const isEnabled = (/** @type {string | undefined} */ value) => value !== 'false';

    const options = {
      align: align || 'start',
      containScroll: true,
      loop: isEnabled(loop),
      dragFree: isEnabled(dragFree),
      startIndex: align === 'center' ? 1 : 0
    };

    const plugins = [isEnabled(autoplay) && Autoplay()].filter(Boolean);

    this.#emblaApi = EmblaCarousel(viewport, options, plugins);
    this.#abortController = new AbortController();

    this.#setupPrevNextButtons();
    this.#setupProgress();
  }

  #destroyCarousel() {
    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#emblaApi) {
      this.#emblaApi.destroy();
      this.#emblaApi = null;
    }

    this.#progressNodes = [];
  }

  reInit() {
    this.#destroyCarousel();
    this.#initCarousel();
  }

  /* ============================================================
   * Navigation Button Click Callback
   * ============================================================ */

  /**
   * Called when a navigation button (prev/next/progress) is clicked.
   * Stops autoplay so the user can browse manually.
   */
  #onNavButtonClick = () => {
    this.#emblaApi?.plugins()?.autoplay?.stop();
  };

  /* ============================================================
   * Arrow Buttons (Prev/Next)
   * ============================================================ */

  #setupPrevNextButtons() {
    const { prevButton, nextButton } = this.refs;
    const signal = this.#abortController?.signal;

    if (prevButton) {
      prevButton.addEventListener(
        'click',
        () => {
          this.#emblaApi.scrollPrev();
          this.#onNavButtonClick();
        },
        { signal },
      );
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        () => {
          this.#emblaApi.scrollNext();
          this.#onNavButtonClick();
        },
        { signal },
      );
    }

    this.#togglePrevNextButtonsState();

    this.#emblaApi
      .on('select', this.#togglePrevNextButtonsState)
      .on('reinit', this.#togglePrevNextButtonsState)
      .on('resize', this.#togglePrevNextButtonsState);
  }

  /**
   * Toggles the disabled state of prev/next buttons based on carousel's ability to scroll.
   */
  #togglePrevNextButtonsState = () => {
    const { prevButton, nextButton, viewport } = this.refs;

    const canScrollPrev = this.#emblaApi.canScrollPrev();
    const canScrollNext = this.#emblaApi.canScrollNext();
    const isScrollable = canScrollPrev || canScrollNext;
    const navContainer =
      prevButton?.closest(selectors.NAV_CONTAINER) || nextButton?.closest(selectors.NAV_CONTAINER);

    if (prevButton) prevButton.disabled = !canScrollPrev;
    if (nextButton) nextButton.disabled = !canScrollNext;
    if (navContainer) navContainer.toggleAttribute('hidden', !isScrollable);
    if (viewport) viewport.toggleAttribute('disabled', !isScrollable);
  };

  /* ============================================================
   * Progress (Dots/Slider)
   * ============================================================ */

  #setupProgress() {
    this.#createProgressItems();
    this.#toggleProgressState();
    this.#updateScrollProgress();

    this.#emblaApi
      .on('reinit', this.#createProgressItems)
      .on('reinit', this.#toggleProgressState)
      .on('reinit', this.#updateScrollProgress)
      .on('resize', this.#createProgressItems)
      .on('resize', this.#toggleProgressState)
      .on('resize', this.#updateScrollProgress)
      .on('scroll', this.#updateScrollProgress)
      .on('select', this.#toggleProgressState);
  }

  #createProgressItems = () => {
    const { progress } = this.refs;

    if (!progress) return;

    const snapList = this.#emblaApi.scrollSnapList();
    const itemCount = snapList.length;

    progress.style.setProperty(selectors.ITEM_COUNT_VAR, Math.max(1, itemCount).toString());

    if (itemCount <= 1) {
      progress.setAttribute('hidden', '');
      progress.innerHTML = '';
      this.#progressNodes = [];

      return;
    }

    progress.removeAttribute('hidden');

    /** @type {number[]} */
    const itemsToRender = progress.dataset.type === 'slider' ? [0] : snapList;

    progress.innerHTML = itemsToRender
      .map(() => `<button class="${selectors.PROGRESS_CLASS}" type="button"></button>`)
      .join('');

    this.#progressNodes = /** @type {HTMLButtonElement[]} */ (
      Array.from(progress.querySelectorAll(`.${selectors.PROGRESS_CLASS}`))
    );

    this.#progressNodes.forEach((progressNode, index) => {
      progressNode.addEventListener(
        'click',
        () => {
          this.#emblaApi.scrollTo(index);
          this.#onNavButtonClick();
        },
        { signal: this.#abortController?.signal },
      );
    });
  };

  #toggleProgressState = () => {
    const previous = this.#emblaApi.previousScrollSnap();
    const selected = this.#emblaApi.selectedScrollSnap();

    this.#progressNodes[previous]?.classList.remove(selectors.PROGRESS_SELECTED_CLASS);
    this.#progressNodes[selected]?.classList.add(selectors.PROGRESS_SELECTED_CLASS);
  };

  #updateScrollProgress = () => {
    const { progress } = this.refs;

    if (progress && progress.dataset.type === 'slider') {
      const scrollProgress = this.#emblaApi.scrollProgress();

      progress.style.setProperty(
        selectors.SCROLL_PROGRESS_VAR,
        Math.max(0, Math.min(1, scrollProgress)).toString(),
      );
    }
  };
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', Carousel);
}
